import React, { useEffect, useState, Suspense } from 'react';
import { CompetitorLogo } from '../../CompetitorLogos';
import type { CompetitorType } from '../../../types';
import {
	Container,
	TransitionContainer,
	BottomFrame,
	TopFrame,
	StyledLogo,
	CompetitorLogoWrapper,
	CompetitorLogoText,
} from './OverflowComparison.styled';

interface OverflowComparisonProps {
	className?: string;
	transitionStart?: number;
	transitionEnd?: number;
	speed?: number;
	blurWidth?: number;
	streamyardView: string;
	competitorView: string;
	competitor: CompetitorType;
	competitorName: string;
}

// Static fallback component
const OverflowComparisonStatic: React.FC<OverflowComparisonProps> = ({
	className = '',
	streamyardView,
	competitorView,
	competitor,
	competitorName,
}) => (
	<Container className={className}>
		<TransitionContainer>
			<BottomFrame>
				<img
					src={competitorView}
					alt={`${competitorName} interface`}
					style={{
						width: 'auto',
						height: 'auto',
						maxWidth: '100%',
						borderRadius: '0.5rem',
						filter: 'grayscale(100%)',
					}}
				/>
				<CompetitorLogoWrapper>
					<CompetitorLogo competitor={competitor} size={20} />
					<CompetitorLogoText>{competitorName}</CompetitorLogoText>
				</CompetitorLogoWrapper>
			</BottomFrame>

			<TopFrame
				style={{
					clipPath: 'polygon(0 0, 20% 0, 20% 100%, 0 100%)', // Start at 20% (80% Competitor image)
				}}
			>
				<StyledLogo isInverted />
				<img
					src={streamyardView}
					alt="StreamYard interface"
					style={{
						width: 'auto',
						height: 'auto',
						maxWidth: '100%',
						borderRadius: '0.5rem',
					}}
				/>
			</TopFrame>
		</TransitionContainer>
	</Container>
);

const OverflowComparison: React.FC<OverflowComparisonProps> = props => {
	const [AnimatedComponent, setAnimatedComponent] =
		useState<React.ComponentType<OverflowComparisonProps> | null>(null);

	useEffect(() => {
		// Dynamic import prevents framer-motion from running during SSR
		// (framer-motion accesses browser APIs that don't exist on the server)
		if (typeof window !== 'undefined') {
			void import('./OverflowComparisonAnimated').then(module => {
				setAnimatedComponent(() => module.default);
			});
		}
	}, []);

	// SSR renders this static version first, then swaps to animated on client
	if (!AnimatedComponent) {
		return <OverflowComparisonStatic {...props} />;
	}

	return (
		<Suspense fallback={<OverflowComparisonStatic {...props} />}>
			<AnimatedComponent {...props} />
		</Suspense>
	);
};

export default OverflowComparison;
